/**
 * Managing the tenant's user accounts.
 *
 * Every action on this screen is wrapped in `<Can>`, and none of that is a
 * security control. The server re-authorises each request; hiding a button
 * only spares the user from discovering they cannot do something by being
 * refused. When the two disagree — a button shown that the server refuses —
 * the server wins and the user sees an error, which is the right failure but
 * an avoidable one.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Can, useCan } from "@hardauto/core-access";
import { ApiError, NetworkError, type ApiClient } from "@hardauto/core-api";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  Input,
  Page,
  Pagination,
  Select,
  useToast,
  type BadgeTone,
  type Column,
} from "@hardauto/core-ui";
import { Field } from "@hardauto/core-forms";

import {
  AdminApi,
  type AdminUser,
  type AdminUserListItem,
  type CreatedUser,
  type Role,
  type UserStatus,
} from "../api";
import { NAMESPACE } from "../i18n";

/** How each account status is coloured. Text carries the meaning; the tone
 *  only reinforces it, so a user who cannot distinguish the colours loses
 *  nothing. */
const STATUS_TONE: Record<UserStatus, BadgeTone> = {
  active: "success",
  pending_verification: "warning",
  suspended: "danger",
  deactivated: "neutral",
};

/** What may follow each status, mirroring the server's state machine.
 *
 * Duplicated from the backend on purpose, and the duplication is bounded: the
 * server refuses anything not in ITS table, so the worst this can do is offer
 * a button that fails. Offering every transition on every row would do that
 * routinely; offering none would make the screen useless. */
const TRANSITIONS: Record<UserStatus, UserStatus[]> = {
  // Activating a pending account is the administrator vouching for someone
  // who has not confirmed their address — an invitation they cannot receive,
  // say. The address stays unverified; only the account opens.
  pending_verification: ["active", "deactivated"],
  active: ["suspended", "deactivated"],
  suspended: ["active", "deactivated"],
  // A closed account can be reopened: closing one by mistake must not mean
  // creating a second account for the same person.
  deactivated: ["active"],
};

/** The wording of a transition. By origin as well as target: opening an
 *  account that never worked is not lifting a block on one that did. */
function actionKey(from: UserStatus, to: UserStatus): string {
  if (to === "active") return from === "pending_verification" ? "activate" : "reinstate";
  if (to === "suspended") return "suspend";
  return "deactivate";
}

const PAGE_SIZE = 25;

export function UsersScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  // Memoised: `load` and the dialogs depend on it, and a fresh instance on
  // every render would re-run their effects forever.
  const api = useMemo(() => new AdminApi(client), [client]);
  const canCreate = useCan("users.account.create");
  const canReadRoles = useCan("access.role.read");

  const [page, setPage] = useState<{
    users: AdminUserListItem[];
    total: number;
    next: string | null;
  }>({
    users: [],
    total: 0,
    next: null,
  });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<UserStatus | "">("");
  // A stack, not a single cursor: cursor pagination can walk forwards on its
  // own but has no way back, so "previous" means popping what we already saw.
  const [cursors, setCursors] = useState<string[]>([]);

  const [creating, setCreating] = useState(false);
  const [statusChange, setStatusChange] = useState<{ user: AdminUser; to: UserStatus } | null>(
    null,
  );
  const [editingRoles, setEditingRoles] = useState<AdminUser | null>(null);

  const resend = async (user: AdminUserListItem) => {
    try {
      const { invitation } = await api.resendInvitation(user.id);
      const key = {
        sent: "users.invitation_sent",
        disabled: "users.invitation_disabled",
        not_needed: "users.invitation_not_needed",
        failed: "users.invitation_failed",
      }[invitation as "sent" | "disabled" | "not_needed" | "failed"];
      toast.show(t(key ?? "users.invitation_failed", { email: user.email ?? "" }), {
        tone: invitation === "sent" ? "success" : "warning",
      });
      void load();
    } catch {
      toast.show(t("users.invitation_failed", { email: user.email ?? "" }), { tone: "danger" });
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const result = await api.listUsers({
        limit: PAGE_SIZE,
        search: search || undefined,
        status: status || undefined,
        after_id: cursors.at(-1),
      });
      setPage({ users: result.users, total: result.total, next: result.next_cursor });
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api, search, status, cursors]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<AdminUserListItem>[] = [
    { key: "email", header: t("users.email"), render: (user) => user.email ?? "—" },
    {
      key: "name",
      header: t("users.name"),
      secondary: true,
      render: (user) => user.display_name ?? "—",
    },
    {
      key: "status",
      header: t("users.status"),
      render: (user) => (
        <Badge tone={STATUS_TONE[user.status]}>{t(`users.status_${user.status}`)}</Badge>
      ),
    },
    {
      key: "tier",
      header: t("users.tier"),
      secondary: true,
      render: (user) => t(`users.tier_${user.tier}`),
    },
    // Only for a reader who may see roles: the server sends null otherwise,
    // and an empty column would read as "nobody holds anything".
    ...(canReadRoles
      ? [
          {
            key: "roles",
            header: t("users.roles"),
            render: (user: AdminUserListItem) =>
              user.roles?.length ? (
                <span className="hardauto-admin-roles">
                  {user.roles.map((role) => (
                    <Badge key={role.key} tone="neutral">
                      {role.name}
                    </Badge>
                  ))}
                </span>
              ) : (
                <span className="hardauto-admin-muted">{t("users.noRoles")}</span>
              ),
          } satisfies Column<AdminUserListItem>,
        ]
      : []),
    {
      key: "actions",
      header: "",
      align: "end",
      render: (user) => (
        <>
          {user.status === "pending_verification" ? (
            <Can permission="users.account.create">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  void resend(user);
                }}
              >
                {t("users.resendInvitation")}
              </Button>
            </Can>
          ) : null}
          <Can permission="access.assignment.manage">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingRoles(user);
              }}
            >
              {t("users.assignRoles")}
            </Button>
          </Can>
          <Can permission="users.account.suspend">
            {TRANSITIONS[user.status].map((target) => (
              <Button
                key={target}
                variant={target === "deactivated" ? "danger" : "ghost"}
                size="sm"
                onClick={() => {
                  setStatusChange({ user, to: target });
                }}
              >
                {t(`users.${actionKey(user.status, target)}`)}
              </Button>
            ))}
          </Can>
        </>
      ),
    },
  ];

  return (
    <Page
      title={t("users.title")}
      description={t("users.description")}
      actions={
        canCreate ? (
          <Button
            variant="primary"
            onClick={() => {
              setCreating(true);
            }}
          >
            {t("users.create")}
          </Button>
        ) : null
      }
    >
      <div className="hardauto-admin-filters">
        <Field label={t("users.searchLabel")}>
          {(props) => (
            <Input
              {...props}
              type="search"
              placeholder={t("users.search")}
              value={search}
              onChange={(event) => {
                // Any filter change invalidates the cursor stack: page three of
                // the old filter is meaningless under the new one.
                setCursors([]);
                setSearch(event.target.value);
              }}
            />
          )}
        </Field>

        <Field label={t("users.filterStatus")}>
          {(props) => (
            <Select
              {...props}
              value={status}
              onChange={(event) => {
                setCursors([]);
                setStatus(event.target.value as UserStatus | "");
              }}
            >
              <option value="">{t("users.allStatuses")}</option>
              {(Object.keys(STATUS_TONE) as UserStatus[]).map((value) => (
                <option key={value} value={value}>
                  {t(`users.status_${value}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <DataTable<AdminUserListItem>
        caption={t("users.caption")}
        columns={columns}
        rows={page.users}
        rowKey={(user) => user.id}
        loading={loading}
        error={
          failed
            ? {
                title: t("users.loadFailed"),
                onRetry: () => void load(),
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{ title: t("users.empty"), description: t("users.emptyDetail") }}
        loadingLabel={t("users.loading")}
      />

      <Pagination
        previousLabel={t("users.previous")}
        nextLabel={t("users.next")}
        summary={t("users.summary", { shown: page.users.length, total: page.total })}
        busy={loading}
        onPrevious={
          cursors.length > 0
            ? () => {
                setCursors((stack) => stack.slice(0, -1));
              }
            : undefined
        }
        onNext={
          page.next
            ? () => {
                setCursors((stack) => [...stack, page.next as string]);
              }
            : undefined
        }
      />

      <CreateUserDialog
        api={api}
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(created) => {
          setCreating(false);
          const note = {
            sent: "users.created_invited",
            skipped: "users.created_not_invited",
            disabled: "users.created_invitations_off",
            failed: "users.created_invitation_failed",
          }[created.invitation as "sent" | "skipped" | "disabled" | "failed"];
          toast.show(t(note ?? "users.created_success", { email: created.email }), {
            // "Created, but the invitation did not go" is not a success to be glossed over.
            tone: created.invitation === "failed" ? "warning" : "success",
          });
          void load();
        }}
      />

      <StatusDialog
        api={api}
        change={statusChange}
        onClose={() => {
          setStatusChange(null);
        }}
        onChanged={() => {
          setStatusChange(null);
          toast.show(t("users.statusChanged"), { tone: "success" });
          void load();
        }}
      />

      <RolesDialog
        api={api}
        user={editingRoles}
        onClose={() => {
          setEditingRoles(null);
        }}
        onSaved={() => {
          setEditingRoles(null);
          toast.show(t("users.rolesSaved"), { tone: "success" });
          // The list shows the roles now, so it must show the new ones.
          void load();
        }}
      />
    </Page>
  );
}

function describe(error: unknown, fallback: string): string {
  if (error instanceof NetworkError) return error.message;
  if (error instanceof ApiError) return error.message;
  return fallback;
}

function CreateUserDialog({
  api,
  open,
  onClose,
  onCreated,
}: {
  api: AdminApi;
  open: boolean;
  onClose: () => void;
  onCreated: (created: CreatedUser) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // Ticked by default: an account nobody is told about is the surprise, not the invitation.
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Reset on OPEN rather than on close, so a failed attempt is still
      // readable while the dialog animates away and a reopened dialog is not
      // pre-filled with someone else's half-typed address.
      setEmail("");
      setName("");
      setInvite(true);
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.createUser({
        email,
        display_name: name || undefined,
        send_invitation: invite,
      });
      onCreated(created);
    } catch (caught) {
      setError(describe(caught, t("error.unexpected")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("users.createTitle")}
      description={t("users.createDescription")}
      closeLabel={t("users.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("users.cancel")}</Button>
          <Button
            variant="primary"
            busy={busy}
            busyLabel={t("users.creating")}
            onClick={() => void submit()}
          >
            {t("users.createSubmit")}
          </Button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}

      <Field label={t("users.email")} required>
        {(props) => (
          <Input
            {...props}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        )}
      </Field>

      <Field label={t("users.name")}>
        {(props) => (
          <Input
            {...props}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        )}
      </Field>

      <Checkbox
        label={t("users.sendInvitation")}
        checked={invite}
        onChange={(event) => {
          setInvite(event.target.checked);
        }}
      />
      <p className="hardauto-admin-muted">{t("users.sendInvitationHint")}</p>
    </Dialog>
  );
}

function StatusDialog({
  api,
  change,
  onClose,
  onChanged,
}: {
  api: AdminApi;
  change: { user: AdminUser; to: UserStatus } | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason("");
    setError(null);
  }, [change]);

  if (!change) {
    return <Dialog open={false} onClose={onClose} title="" closeLabel={t("users.close")} />;
  }

  const who = change.user.display_name ?? change.user.email ?? change.user.id;
  const key = actionKey(change.user.status, change.to);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.changeStatus(change.user.id, change.to, reason || undefined);
      onChanged();
    } catch (caught) {
      setError(describe(caught, t("error.unexpected")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(`users.${key}Title`, { name: who })}
      // Spells out the consequence rather than asking "are you sure?". A
      // confirmation that does not say what will happen trains people to click
      // through it.
      description={t(`users.${key}Body`)}
      closeLabel={t("users.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("users.cancel")}</Button>
          <Button
            variant={change.to === "active" ? "primary" : "danger"}
            busy={busy}
            busyLabel={t("users.saving")}
            onClick={() => void submit()}
          >
            {t("users.confirm")}
          </Button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}

      <Field label={t("users.reason")}>
        {(props) => (
          <Input
            {...props}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        )}
      </Field>
    </Dialog>
  );
}

function RolesDialog({
  api,
  user,
  onClose,
  onSaved,
}: {
  api: AdminApi;
  user: AdminUser | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setError(null);
    void (async () => {
      try {
        const [all, held] = await Promise.all([api.listRoles(), api.userRoles(user.id)]);
        if (cancelled) return;
        setRoles(all.roles);
        setSelected(new Set(held.roles.map((role) => role.key)));
      } catch (caught) {
        if (!cancelled) setError(describe(caught, t("error.unexpected")));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, user, t]);

  if (!user) {
    return <Dialog open={false} onClose={onClose} title="" closeLabel={t("users.close")} />;
  }

  const who = user.display_name ?? user.email ?? user.id;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.assignRoles(user.id, [...selected]);
      onSaved();
    } catch (caught) {
      setError(describe(caught, t("error.unexpected")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("users.rolesTitle", { name: who })}
      description={t("users.rolesDescription")}
      closeLabel={t("users.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("users.cancel")}</Button>
          <Button
            variant="primary"
            busy={busy}
            busyLabel={t("users.saving")}
            onClick={() => void submit()}
          >
            {t("users.save")}
          </Button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}

      <ul className="hardauto-admin-checklist">
        {roles.map((role) => (
          <li key={role.key}>
            <Checkbox
              label={role.name}
              checked={selected.has(role.key)}
              onChange={(event) => {
                setSelected((current) => {
                  // A new Set, not a mutation: React compares by reference and
                  // a mutated one re-renders nothing.
                  const next = new Set(current);
                  if (event.target.checked) next.add(role.key);
                  else next.delete(role.key);
                  return next;
                });
              }}
            />
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
